import * as React from 'react';
import axios from 'axios';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import {
    format,
    parseISO,
    subDays,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfYear,
} from 'date-fns';
import useAuthorize from '../hooks/useAuthorize';
import { DASHBOARD_MODULE_ROLES } from '../roles/portalRoles';
import { getAdminApiBaseUrl } from '../utils/adminApiBaseUrl';
import { AnalyticsFreshnessNote } from '../utils/AnalyticsFreshnessNote';
import ReprocessedClickDetailModal, { ReprocessedClickChannel } from './ReprocessedClickDetailModal';

interface ReprocessedListingRow {
    listing_id: number;
    title: string;
    seller_id: number;
    seller_name: string;
    category_name: string;
    sub_category_name: string;
    status: string;
    published_at: string | null;
    view_inquire_clicks: number;
    detail_page_views: number;
    message_clicks: number;
    whatsapp_clicks: number;
    call_clicks: number;
    total_inquiry_clicks: number;
}

type SummarySortField =
    | 'title'
    | 'seller_name'
    | 'published_at'
    | 'view_inquire_clicks'
    | 'detail_page_views'
    | 'message_clicks'
    | 'whatsapp_clicks'
    | 'call_clicks'
    | 'total_inquiry_clicks';

type SortOrder = 'asc' | 'desc';

const METRIC_COLUMNS: Array<{
    field: SummarySortField;
    channel?: ReprocessedClickChannel;
    label: string;
}> = [
    { field: 'view_inquire_clicks', channel: 'view_inquire', label: 'View & Inquire' },
    { field: 'detail_page_views', channel: 'detail_view', label: 'Detail Views' },
    { field: 'message_clicks', channel: 'message', label: 'Send Inquiry' },
    { field: 'whatsapp_clicks', channel: 'whatsapp', label: 'WhatsApp' },
    { field: 'call_clicks', channel: 'call', label: 'Call' },
    { field: 'total_inquiry_clicks', channel: 'all', label: 'Total Inquiry' },
];

const ClickableCount: React.FC<{
    count: number;
    onClick: () => void;
}> = ({ count, onClick }) => {
    if (count <= 0) {
        return <Typography variant="body2">0</Typography>;
    }

    return (
        <Link
            component="button"
            variant="body2"
            underline="hover"
            onClick={onClick}
            sx={{ fontWeight: 600, cursor: 'pointer' }}
        >
            {count.toLocaleString()}
        </Link>
    );
};

const DashboardReprocessed = () => {
    useAuthorize(DASHBOARD_MODULE_ROLES);

    const getDefaultDateRange = () => {
        const now = new Date();
        return {
            fromDate: format(startOfYear(now), 'yyyy-MM-dd'),
            toDate: format(now, 'yyyy-MM-dd'),
        };
    };

    const defaultDates = getDefaultDateRange();

    const [fromDate, setFromDate] = React.useState(defaultDates.fromDate);
    const [toDate, setToDate] = React.useState(defaultDates.toDate);
    const [quickRange, setQuickRange] = React.useState('');
    const [rows, setRows] = React.useState<ReprocessedListingRow[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [total, setTotal] = React.useState(0);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(25);
    const [sort, setSort] = React.useState<SummarySortField>('total_inquiry_clicks');
    const [order, setOrder] = React.useState<SortOrder>('desc');
    const [dataThrough, setDataThrough] = React.useState<string | null>(null);
    const [lastUpdatedAt, setLastUpdatedAt] = React.useState<string | null>(null);
    const [detailOpen, setDetailOpen] = React.useState(false);
    const [selectedListing, setSelectedListing] = React.useState<ReprocessedListingRow | null>(null);
    const [selectedChannel, setSelectedChannel] = React.useState<ReprocessedClickChannel | null>(null);
    const [selectedChannelLabel, setSelectedChannelLabel] = React.useState('');
    const [selectedExpectedCount, setSelectedExpectedCount] = React.useState(0);

    React.useEffect(() => {
        const fetchSummary = async () => {
            setLoading(true);

            try {
                const response = await axios.get(`${getAdminApiBaseUrl()}/analytics/reprocessed-listings`, {
                    params: {
                        from_date: fromDate,
                        to_date: toDate,
                        status: 'approved',
                        limit: rowsPerPage,
                        offset: page * rowsPerPage,
                        sort,
                        order,
                    },
                });

                setRows(Array.isArray(response.data?.data) ? response.data.data : []);
                setTotal(Number(response.data?.total) || 0);
                setDataThrough(response.data?.data_through || response.headers?.['x-analytics-data-through'] || null);
                setLastUpdatedAt(response.data?.last_updated_at || response.headers?.['x-analytics-last-updated'] || null);
            } catch (error) {
                console.error('Failed to load reprocessed listings analytics:', error);
                setRows([]);
                setTotal(0);
            } finally {
                setLoading(false);
            }
        };

        fetchSummary();
    }, [fromDate, toDate, page, rowsPerPage, sort, order]);

    const handleQuickRangeChange = (event: any) => {
        const range = event.target.value;
        setQuickRange(range);

        if (!range) {
            return;
        }

        const now = new Date();
        let from = now;
        let to = now;

        switch (range) {
            case 'today':
                from = to = now;
                break;
            case 'yesterday':
                from = to = subDays(now, 1);
                break;
            case 'last7':
                from = subDays(now, 6);
                to = now;
                break;
            case 'thisWeek':
                from = startOfWeek(now, { weekStartsOn: 1 });
                to = endOfWeek(now, { weekStartsOn: 1 });
                break;
            case 'thisMonth':
                from = startOfMonth(now);
                to = endOfMonth(now);
                break;
            default:
                break;
        }

        setFromDate(format(from, 'yyyy-MM-dd'));
        setToDate(format(to, 'yyyy-MM-dd'));
        setPage(0);
    };

    const handleSort = (field: SummarySortField) => {
        const isSameField = sort === field;
        setSort(field);
        setOrder(isSameField ? (order === 'asc' ? 'desc' : 'asc') : 'desc');
        setPage(0);
    };

    const handleMetricClick = (
        listing: ReprocessedListingRow,
        channel: ReprocessedClickChannel,
        label: string,
        count: number
    ) => {
        if (count <= 0) {
            return;
        }
        setSelectedListing(listing);
        setSelectedChannel(channel);
        setSelectedChannelLabel(label);
        setSelectedExpectedCount(count);
        setDetailOpen(true);
    };

    const formatPublishedDate = (value: string | null) => {
        if (!value) {
            return '—';
        }
        try {
            return format(parseISO(value), 'MMM d, yyyy');
        } catch {
            return value;
        }
    };

    return (
        <Box sx={{ padding: 2 }}>
            <Card>
                <CardHeader
                    title="Reprocessed Listings Analytics"
                    subheader="Per-listing engagement metrics from nightly audit rollups. Click any count to see customer-level click details."
                />
                <CardContent>
                    {dataThrough && (
                        <AnalyticsFreshnessNote dataThrough={dataThrough} lastUpdatedAt={lastUpdatedAt} />
                    )}

                    <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={fromDate}
                                onChange={(event) => {
                                    setFromDate(event.target.value);
                                    setQuickRange('');
                                    setPage(0);
                                }}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="To Date"
                                type="date"
                                value={toDate}
                                onChange={(event) => {
                                    setToDate(event.target.value);
                                    setQuickRange('');
                                    setPage(0);
                                }}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Quick Range</InputLabel>
                                <Select value={quickRange} label="Quick Range" onChange={handleQuickRangeChange}>
                                    <MenuItem value="">Custom</MenuItem>
                                    <MenuItem value="today">Today</MenuItem>
                                    <MenuItem value="yesterday">Yesterday</MenuItem>
                                    <MenuItem value="last7">Last 7 Days</MenuItem>
                                    <MenuItem value="thisWeek">This Week</MenuItem>
                                    <MenuItem value="thisMonth">This Month</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : rows.length === 0 ? (
                        <Box sx={{ py: 6, textAlign: 'center' }}>
                            <Typography color="text.secondary">No approved reprocessed listings found.</Typography>
                        </Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sortDirection={sort === 'title' ? order : false}>
                                                <TableSortLabel active={sort === 'title'} direction={sort === 'title' ? order : 'asc'} onClick={() => handleSort('title')}>
                                                    Listing
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell sortDirection={sort === 'seller_name' ? order : false}>
                                                <TableSortLabel active={sort === 'seller_name'} direction={sort === 'seller_name' ? order : 'asc'} onClick={() => handleSort('seller_name')}>
                                                    Seller
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>Category</TableCell>
                                            <TableCell sortDirection={sort === 'published_at' ? order : false}>
                                                <TableSortLabel active={sort === 'published_at'} direction={sort === 'published_at' ? order : 'asc'} onClick={() => handleSort('published_at')}>
                                                    Published
                                                </TableSortLabel>
                                            </TableCell>
                                            {METRIC_COLUMNS.map(column => (
                                                <TableCell key={column.field} align="right" sortDirection={sort === column.field ? order : false}>
                                                    <TableSortLabel
                                                        active={sort === column.field}
                                                        direction={sort === column.field ? order : 'asc'}
                                                        onClick={() => handleSort(column.field)}
                                                    >
                                                        {column.label}
                                                    </TableSortLabel>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map(row => (
                                            <TableRow key={row.listing_id} hover>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {row.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        #{row.listing_id}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{row.seller_name}</TableCell>
                                                <TableCell>
                                                    {row.category_name}
                                                    {row.sub_category_name ? ` / ${row.sub_category_name}` : ''}
                                                </TableCell>
                                                <TableCell>{formatPublishedDate(row.published_at)}</TableCell>
                                                {METRIC_COLUMNS.map(column => {
                                                    const metricCount = Number(row[column.field]) || 0;
                                                    return (
                                                    <TableCell key={column.field} align="right">
                                                        <ClickableCount
                                                            count={metricCount}
                                                            onClick={() => handleMetricClick(
                                                                row,
                                                                column.channel as ReprocessedClickChannel,
                                                                column.label,
                                                                metricCount
                                                            )}
                                                        />
                                                    </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={(_event, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(event) => {
                                    setRowsPerPage(parseInt(event.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                            />
                        </>
                    )}
                </CardContent>
            </Card>

            <ReprocessedClickDetailModal
                open={detailOpen}
                listingId={selectedListing?.listing_id || null}
                listingTitle={selectedListing?.title || 'Listing'}
                channel={selectedChannel}
                channelLabel={selectedChannelLabel}
                expectedCount={selectedExpectedCount}
                fromDate={fromDate}
                toDate={toDate}
                onClose={() => setDetailOpen(false)}
            />
        </Box>
    );
};

export default DashboardReprocessed;
